// A "Kind" is a rich type describing more aspects of a user input besides just bool/number/string.
// This is used throughout the rich type system (FieldSpec, RichType etc.), for things including unit
// handling, documentation and presentation.

export type DataType = 'boolean' | 'number' | 'string'
export type Alignment = 'center' | 'left' | 'right'

export interface BaseKindDefinition {
  title: string
}

// Specialized definitions that preserve literal types.
export interface BooleanKindDefinition extends BaseKindDefinition {
  dataType: 'boolean'
  align: 'left'
  isNumeric: false
}

export interface NumericKindDefinition extends BaseKindDefinition {
  dataType: 'number'
  align: 'right'
  isNumeric: true
  unitOptions?: Record<string, number>
  defaultUnit?: (metric: boolean) => string | null
}

export interface StringKindDefinition extends BaseKindDefinition {
  dataType: 'string'
  align: 'left'
  isNumeric: false
}

export type KindDefinition =
  | BooleanKindDefinition
  | NumericKindDefinition
  | StringKindDefinition

function createNumericKind(
  def: Omit<NumericKindDefinition, 'dataType' | 'align' | 'isNumeric'>,
): NumericKindDefinition {
  return {
    dataType: 'number' as const,
    align: 'right',
    isNumeric: true as const,
    ...def,
  }
}

function createStringKind(
  def: Omit<StringKindDefinition, 'dataType' | 'align' | 'isNumeric'>,
): StringKindDefinition {
  return {
    dataType: 'string' as const,
    align: 'left',
    isNumeric: false as const,
    ...def,
  }
}

const LB = 0.45359237 as const // kg
const IN = 25.4 as const // mm
const FT = 304.8 as const // mm - NB: Don't use 12*IN as that leads to rounding error!

// Centralized definitions for each kind
export const KindDefinitions = {
  // A placeholder for custom components or logic that doesn't fit into other categories
  placeholder: createStringKind({
    title: 'Placeholder',
  }),

  boolean: {
    title: 'Boolean',
    dataType: 'boolean',
    align: 'left',
    isNumeric: false as const,
  },

  integer: createNumericKind({
    title: 'Integer',
  }),

  unitless: createNumericKind({
    title: 'Unitless',
  }),

  percent: createNumericKind({
    title: 'Percent',
  }),

  money: createNumericKind({
    title: 'Amount',
  }),

  money_per_mass: createNumericKind({
    // _ is placeholder for actual currency symbol
    title: 'Money per Mass',
    defaultUnit: (metric: boolean) => (metric ? '_/kg' : '_/lb'),
    unitOptions: {
      '_/g': 1 / 0.001,
      '_/kg': 1.0,
      '_/lb': 1 / LB,
      '_/oz': 1 / (LB / 16),
    },
  }),

  money_per_hour: createNumericKind({
    // _ is placeholder for actual currency symbol
    title: 'Money per Mass',
    defaultUnit: () => '_/h',
    unitOptions: { '_/h': 1 },
  }),

  length: createNumericKind({
    title: 'Length',
    defaultUnit: (metric: boolean) => (metric ? 'mm' : 'in'),
    unitOptions: { mm: 1, cm: 10, m: 1000, in: IN, ft: FT },
  }),

  volume: createNumericKind({
    title: 'Volume',
    defaultUnit: (metric: boolean) => (metric ? 'cm³' : 'in³'),
    unitOptions: {
      'mm³': 1,
      'cm³': 10 ** 3,
      l: 100 ** 3,
      'm³': 1000 ** 3,
      'in³': IN ** 3,
      'ft³': FT ** 3,
      // 'gal': IN**3 * 231, // US gallon = 231 in³ ~ 3.785 l
      // 'gal': 4.54609e6, // Imperial gallon exactly 4.54609 liters
    },
  }),

  mass: createNumericKind({
    title: 'Mass',
    defaultUnit: (metric: boolean) => (metric ? 'kg' : 'lb'),
    unitOptions: { kg: 1, g: 0.001, lb: LB, oz: LB / 16 },
  }),

  angle: createNumericKind({
    title: 'Angle',
    defaultUnit: () => 'deg',
    unitOptions: { deg: 1, rad: 180 / Math.PI },
  }),

  current: createNumericKind({
    title: 'Current',
    defaultUnit: () => 'A',
    unitOptions: { kA: 1000, A: 1, mA: 0.001, µA: 0.000001 },
  }),

  watts: createNumericKind({
    title: 'Watts',
    defaultUnit: () => 'W',
    unitOptions: { W: 1, kW: 1000, MW: 1000000 },
  }),

  volts: createNumericKind({
    title: 'Volts',
    defaultUnit: () => 'V',
    unitOptions: { V: 1, kV: 1000 },
  }),

  gas_flow: createNumericKind({
    title: 'Gas Flow Rate',
    defaultUnit: (metric: boolean) => (metric ? 'm³/h' : 'ft³/min'),
    unitOptions: { 'm³/h': 1, 'ft³/min': 1.69901, 'l/s': 3.6 },
  }),

  feedrate: createNumericKind({
    title: 'Feedrate',
    defaultUnit: (metric: boolean) => (metric ? 'mm/min' : 'in/min'),
    unitOptions: { 'mm/min': 1, 'in/min': IN },
  }),

  acceleration: createNumericKind({
    title: 'Acceleration',
    defaultUnit: (metric: boolean) => (metric ? 'mm/s²' : 'in/s²'),
    unitOptions: {
      'm/s²': 1,
      'mm/s²': 0.001,
      'ft/s²': FT / 1000,
      'in/s²': IN / 1000,
    },
  }),

  rotation_speed: createNumericKind({
    title: 'Rotation Speed',
    defaultUnit: () => 'rpm',
    unitOptions: { rpm: 1, rps: 1 / 60 },
  }),

  surface_speed: createNumericKind({
    title: 'Surface Speed',
    defaultUnit: (metric: boolean) => (metric ? 'm/min' : 'ft/min'),
    unitOptions: { 'm/min': 1, 'ft/min': FT / 1000 },
  }),

  chip_load: createNumericKind({
    title: 'Chip Load',
    defaultUnit: (metric: boolean) => (metric ? 'mm/tooth' : 'in/tooth'),
    unitOptions: { 'mm/tooth': 1, 'in/tooth': IN },
  }),

  hardness: createNumericKind({
    title: 'Hardness',
    defaultUnit: () => 'hb',
    // Only one hardness scale is provided as conversion between different hardness scales is non-linear.
    unitOptions: { hb: 1 },
  }),

  density: createNumericKind({
    title: 'Density',
    defaultUnit: (metric: boolean) => (metric ? 'kg/m³' : 'lb/ft³'),
    unitOptions: {
      'kg/m³': 1,
      'g/cm³': 1000,
      'lb/ft³': LB / (FT / 1000) ** 3,
      'lb/in³': LB / (IN / 1000) ** 3,
    },
  }),

  duration: createNumericKind({
    title: 'Duration',
    defaultUnit: () => 's',
    unitOptions: { s: 1, min: 60, h: 3600 },
  }),

  text: createStringKind({
    title: 'Text',
  }),

  enum: createStringKind({
    title: 'Enumeration',
  }),

  asset: createStringKind({
    title: 'Asset',
  }),

  object: createStringKind({
    title: 'Object',
  }),

  array: createStringKind({
    title: 'Array',
  }),

  datetime: createStringKind({
    title: 'Datetime',
  }),

  guid: createStringKind({
    title: 'GUID',
  }),

  url: createStringKind({
    title: 'URL',
  }),

  segments: createStringKind({
    title: 'Segments',
  }),

  group: {
    title: 'Group',
    dataType: 'boolean', // expanded or not
    align: 'left',
    isNumeric: false as const,
  },
} as const

// Derive the KindId type from the keys of KindDefinitions
export type KindId = keyof typeof KindDefinitions

export type NumericKindId = {
  [K in keyof typeof KindDefinitions]: (typeof KindDefinitions)[K]['dataType'] extends 'number'
    ? K
    : never
}[keyof typeof KindDefinitions]

export type NumericKindId2 = {
  [K in keyof typeof KindDefinitions]: (typeof KindDefinitions)[K]['isNumeric'] extends true
    ? K
    : never
}[keyof typeof KindDefinitions]

// Helper functions simply look up values from KindDefinitions

export function getKindDataType(kind: KindId): DataType {
  return KindDefinitions[kind].dataType
}

export function getKindAlign(kind: KindId): Alignment {
  return KindDefinitions[kind].align
}

export function isKindNumeric(kind: KindId): kind is NumericKindId {
  return KindDefinitions[kind].isNumeric
}

export function getDefaultUnit(kind: KindId, metric: boolean): string | null {
  if (!isKindNumeric(kind)) {
    return null
  }
  const def = KindDefinitions[kind]
  return def.defaultUnit ? def.defaultUnit(metric) : null
}

export function getUnitOptions(
  kind: NumericKindId,
): string[] | Record<string, number> | undefined {
  return KindDefinitions[kind].unitOptions
}

// Computes the conversion factor between two units for a given kind.
export function getUnitConversionFactor(
  kind: KindId,
  fromUnit: string,
  toUnit: string,
): number | undefined {
  if (!isKindNumeric(kind)) {
    return undefined
  }
  const options = getUnitOptions(kind)
  if (!options) {
    return undefined // Unitless - no conversion necessary
  }
  if (fromUnit == toUnit) {
    return undefined // No conversion necessary
  }
  if (!(fromUnit in options) || !(toUnit in options)) {
    throw new Error(
      `Unsupported conversion units for ${kind}: ${fromUnit} or ${toUnit}`,
    )
  }
  return options[fromUnit] / options[toUnit]
}

export function getDefaultConversionFactor(kind: KindId, toMetric: boolean) {
  if (!isKindNumeric(kind)) {
    return undefined
  }
  const fromUnit = getDefaultUnit(kind, !toMetric)
  const toUnit = getDefaultUnit(kind, toMetric)
  if (!fromUnit || !toUnit) {
    return undefined
  }
  return getUnitConversionFactor(kind, fromUnit, toUnit)
}

export function isApprox(a, b, eps = 1e-9) {
  return Math.abs(a - b) <= eps
}

export function kindEqual(kind: KindId, a, b, eps = 1e-9) {
  if (isKindNumeric(kind)) {
    return Math.abs(a - b) <= eps
  }
  return a === b
}

export function getKindPrecision(kind: KindId, metric: boolean) {
  switch (kind) {
    case 'length':
      return metric ? 2 : 4
    case 'volume':
      return metric ? 0 : 2
    case 'angle':
      return 1
    case 'rotation_speed':
      return 0
    case 'feedrate':
      return metric ? 0 : 2
    case 'surface_speed':
      return 0
  }
  return 4
}

export function getValueForDisplay(
  val: any,
  kind: KindId,
  metric: boolean,
  useGrouping: boolean = false,
): string {
  if (val === '' || val === null) {
    return ''
  }

  if (!isKindNumeric(kind)) {
    return val
  }

  return Intl.NumberFormat(undefined, {
    maximumFractionDigits: getKindPrecision(kind, metric),
    useGrouping: useGrouping,
  }).format(val)
}
